import React from 'react';

/* Label above control, always. Never beside, never floating, never
   placeholder-as-label. Help text is present on every field, not only the
   confusing ones — a field with no explanation reads as a field with no
   consequences. Error text REPLACES help text in the same slot so the layout
   never shifts.

   ## The chrome belongs to the default input, not to the field

   Fixed 2026-08-01, found by looking at the thing rather than at the code.

   `FormField` drew its own `--bg-inset` box with a border and a 32px height around
   whatever it contained. That is right for its default `<input>`, which is a bare,
   borderless element that needs a container to look like a control. It is wrong for a
   supplied child — `Select`, `Textarea` and `SecretField` each bring their own border,
   their own background and their own focus ring — so `<FormField><Select/></FormField>`
   rendered **a bordered control inside a bordered box**, the second one stretched to the
   field's full width with the real control sitting at its left end.

   That is the pattern `MarketsScreen` uses for both of its controls, so it was shipping.

   So: chrome only when this component owns the control. When `children` are supplied the
   wrapper is layout-only, the child's width decides the field's width, and the error
   state is carried by a 1px `--loss` ring drawn *around* whatever the child is — which
   works without knowing anything about it. */

export function FormField({
  label, value, defaultValue, onChange, unit, help, error, numeric = false,
  type = 'text', placeholder, disabled, id, width, style, children, ...rest
}) {
  const fid = id || 'ff-' + String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const bare = Boolean(children);

  const box = bare
    ? {
        display: 'inline-flex', alignItems: 'center', borderRadius: 'var(--radius-control)',
        // Outlines the child rather than boxing it, so an error is visible on a control
        // this component cannot style from the outside.
        boxShadow: error ? '0 0 0 1px var(--loss)' : undefined,
      }
    : {
        display: 'flex', alignItems: 'center', height: 'var(--control-h, 32px)',
        background: 'var(--bg-inset)', borderRadius: 'var(--radius-control)',
        boxShadow: 'var(--edge-inset)',
        border: '1px solid ' + (error ? 'var(--loss)' : 'var(--border-default)'),
      };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
      // A supplied child sizes itself; only the default input needs to fill.
      width: width || (bare ? 'auto' : '100%'),
      ...style,
    }}>
      <label htmlFor={fid} style={{
        fontSize: 'var(--label-size)', lineHeight: 'var(--label-line)', fontWeight: 'var(--label-weight)',
        letterSpacing: 'var(--label-tracking)', textTransform: 'uppercase', color: 'var(--text-tertiary)',
      }}>{label}</label>

      <div style={box} className={bare ? undefined : 'jmt-field'}>
        {children || (
          <input
            id={fid} type={type} value={value} defaultValue={defaultValue} onChange={onChange}
            placeholder={placeholder} disabled={disabled}
            style={{
              flex: 1, minWidth: 0, height: '100%', padding: '0 var(--space-12)',
              background: 'transparent', border: 'none', outline: 'none',
              color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
              fontFamily: 'var(--font-ui)',
              fontSize: numeric ? 'var(--num-md-size)' : 'var(--body-size)',
              fontVariantNumeric: numeric ? 'tabular-nums slashed-zero' : 'normal',
              textAlign: numeric ? 'right' : 'left',
            }}
            {...rest}
          />
        )}
        {unit ? (
          <span style={{
            padding: '0 var(--space-12) 0 var(--space-4)', color: 'var(--text-tertiary)',
            fontSize: 'var(--num-md-size)', fontVariantNumeric: 'tabular-nums',
          }}>{unit}</span>
        ) : null}
      </div>

      <div style={{
        fontSize: 'var(--caption-size)', lineHeight: 'var(--caption-line)',
        color: error ? 'var(--loss)' : 'var(--text-secondary)', minHeight: 'var(--caption-line)',
      }}>{error || help}</div>

      <style>{`.jmt-field:focus-within{border-color:var(--border-strong);box-shadow:var(--focus-ring)}`}</style>
    </div>
  );
}
